import { FormStore, GlobalStore } from '@roc/feature-app-core';
import { LoanService, LoanStore } from '@roc/feature-loans';
import { CLOSING } from '@roc/feature-utils';
import { FileUpload } from '@roc/ui';
import { flow, makeObservable, observable } from 'mobx';
import { DocumentSectionNames, DocumentService, DocumentStore } from '../../documents';
import { LoanActionsService } from '../services/loanActionsService';
import { LoanActionsStore } from './loanActionsStore';
import { TaskV2Category } from 'libs/feature-general-tasks/src/generalTasks/types/generalTasksTypes';

const REQUIRED_FIELD = 'This field is required.';

const form = {
  fields: {
    removePreviousClosingDocs: {
      value: '',
      error: null,
      rule: '',
      message: REQUIRED_FIELD,
    },
    uploadGeneratedDocsAnswer: {
      value: '',
      error: null,
      rule: 'required',
      message: REQUIRED_FIELD,
    },
    uploadGeneratedDocsComment: {
      value: '',
      error: null,
      rule: 'required',
      message: REQUIRED_FIELD,
    },
  },
  meta: {
    isValid: false,
    error: null,
  },
};

export class UploadClosingDocumentsStore extends FormStore {
  private globalStore: GlobalStore;
  private loanStore: LoanStore;
  private documentStore: DocumentStore;
  private loanActionsStore: LoanActionsStore;

  private loanActionsService: LoanActionsService;
  private documentService: DocumentService;

  constructor(
    globalStore: GlobalStore,
    loanStore: LoanStore,
    documentStore: DocumentStore,
    loanActionsStore: LoanActionsStore
  ) {
    super(form, globalStore);
    this.globalStore = globalStore;
    this.loanStore = loanStore;
    this.documentStore = documentStore;
    this.loanActionsStore = loanActionsStore;

    this.loanActionsService = new LoanActionsService();
    this.documentService = new DocumentService();

    makeObservable(this, {
      uploadFiles: flow,
      uploadClosingDocuments: flow,
    });
  }

  *uploadFiles(fileUploads: FileUpload[]) {
    const { loanId } = this.loanStore.loanDetails;
    const section = this.documentStore.documentSections.find(
      s => s.sectionName == TaskV2Category.CLOSING_DOCUMENTS
    );
    const document = this.documentStore.getDocumentFromName(
      CLOSING,
      TaskV2Category.CLOSING_DOCUMENTS
    );
    const loanDocuments = this.documentStore.mapFilesToLoanDocs(
      fileUploads,
      loanId,
      section?.sectionId,
      null,
      document,
      null,
      TaskV2Category.CLOSING_DOCUMENTS
    );

    const formData = new FormData();
    for (const fileUpload of fileUploads) {
      const file = fileUpload.file;
      const blob = file.slice(0, file.size, file.type);
      const newFile = new File([blob], fileUpload.name, { type: file.type });
      formData.append('file', newFile);
    }
    formData.append('loanDocuments', JSON.stringify(loanDocuments));

    const response = yield this.documentService.uploadClosingDocumentsCombined(
      formData,
      false
    );
    const taskIds = (response?.data ?? []).map(r => r?.taskId);
    return taskIds;
  }
  *uploadClosingDocuments(fileUploads: FileUpload[]) {
    try {
      const { loanId } = this.loanStore.loanDetails;
      const answers = this.getFormValues();
      if (answers.removePreviousClosingDocs) {
        yield this.loanActionsService.removePreviousFromClosingDocuments(
          loanId
        );
      }
      const taskIds = yield this.uploadFiles(fileUploads);
      yield this.loanActionsService.updateUploadClosingDocumentsAnswers(
        loanId,
        {
          ...answers,
          taskIds,
        }
      );
      this.loanActionsStore.currentAction = null;
    } catch (e) {
      console.log({ e });
      this.globalStore.notificationStore.showErrorNotification({
        message: 'Error while making request',
      });
    }
  }
}
